"use client"
// components/Hero.tsx
import { usePageSettings } from '@/components/contexts/PageSettingsContext'
import {
    Headline,
    Subheadline,
    Container,
    ContentWrapper,
    CTAGroup,
    CTAButton,
} from '@/components/ui'
import { BUTTON_IDS } from '@/lib/constants'
import { APP_LOGIN_URL } from '@/lib/redirect-utils'
import { handleInstantLogin, handleEmailSignup } from '@/lib/auth-utils'
import Image from 'next/image'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import React, { useState, useRef, useEffect } from 'react'
import { EmailSignupSuccess } from '@/components/ui/email-signup-success'
// const HeroVideo = dynamic(() => import('./HeroVideo'))


interface HeroHomepageProps {
    shouldShowVideo?: boolean,
    className?: string,
    title?: string,
    subtitle?: string,
    CTA?:string
    emailPlaceholder?: string
    submitButtonText?: string
    htmlTitle?: boolean,
    CTA_BOOK_DEMO?: string
    CTA_HREF?: string
    CTA_BOOK_DEMO_HREF?: string
    customCTAs?: React.ReactNode
}

const HeroW45 = ({
    shouldShowVideo = true,
    className = " gradient-bg-12",
    title = "Get Your Projects Under Control",
    subtitle = "Turn Voice Notes Into Shippable Tasks, 4x faster than typing, auto-polished for the backlog",
    CTA="Try Voice Tasking Free",
    emailPlaceholder = "Enter your email",
    submitButtonText,
    htmlTitle = false,
    CTA_BOOK_DEMO="Book Demo",
    CTA_HREF,
    CTA_BOOK_DEMO_HREF,
    customCTAs,
}: HeroHomepageProps) => {
    const { enableHeroGoogleLogin = false, enableEmailSignup = false, emailSignupVariant } = usePageSettings()
    const showEmailInput = enableEmailSignup && (emailSignupVariant === 'lean' || emailSignupVariant === 'instant')
    const isInstantVariant = enableEmailSignup && emailSignupVariant === 'instant'
    
    // Handle smooth scroll for anchor links
    const handleAnchorClick = (e: React.MouseEvent<HTMLElement>, href?: string) => {
        if (href && href.startsWith('#')) {
            e.preventDefault()
            const element = document.querySelector(href)
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }
    }
    
    // Email signup state for lean/instant variants
    const [email, setEmail] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)
    const emailInputRef = useRef<HTMLInputElement>(null)
    
    // Auto-focus for lean and instant variants
    useEffect(() => {
      if (showEmailInput && emailInputRef.current) {
        setTimeout(() => {
          emailInputRef.current?.focus()
        }, 100)
      }
    }, [showEmailInput])
    
    // Email validation
    const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    }

    
    // Handle email signup submission
    const handleEmailSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      
      if (!email.trim()) {
        setError('Please enter your email address')
        return
      }
      
      if (!validateEmail(email)) {
        setError('Please enter a valid email address')
        return
      }
      
      setIsLoading(true)
      
      try {
        if (isInstantVariant) {
          // Instant login - call API and redirect
          const posthogInstance = typeof window !== 'undefined' ? (window as any).posthog : undefined
          const redirectUrl = await handleInstantLogin(email, posthogInstance)
          
          // Add a small delay to show success before redirect
          setTimeout(() => {
            window.location.href = redirectUrl
          }, 500)
        } else {
          // Lean variant - call email signup API and show success message
          await handleEmailSignup(email)
          setSuccess(true)
          setEmail('')
        }
      } catch (err: any) {
        console.error('❌ Error:', err)
        setError(err?.message || 'Something went wrong. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    return (

        <>
            <section className='VWO_466_disable_on_vwo py-0  hero mx-auto max-w-[1808px] sm:px-[1.5rem] px-3 ' >
                <div className="section_container w-full">
                    {/* Hero Content Container with specified padding */}
                    <Container className="md:max-h-[100%] gap-0 gradient-container no-gradient-after gradient-bg-12" variant="hero">
                        {/* Headlines Container with specified gap */}
                        <ContentWrapper gap="lg" className="z-10 relative sm:px-0 px-2">
                            <Headline variant='hero' htmlContent={htmlTitle}>{title}</Headline>
                            <Subheadline variant='hero'>
                                {subtitle}
                            </Subheadline>
                        </ContentWrapper>

                        {/* Main Screen Image */}
                        <div className={`${shouldShowVideo ? 'mainscreen-container' : 'min-h-[0px] sm:min-h-[50px] md:min-h-[80px]'}  order-2 flex-grow flex sm:items-end items-start justify-center w-full relative `}>

                            <div className={`  object-contain relative top-7 h-fit sm:top-0 
                                            max-w-[95%] w-full lg:max-w-[70%] sm:max-w-[95%] 
                                            sm:w-full rounded-t-lg `}>
                                <Image width={600} height={600} src="/images/sections/hero_desktop_w41.avif" alt="hero image" className="hidden sm:block w-full h-full rounded-lg object-contain" />
                                
                                <Image width={600} height={600} src="/images/sections/hero_mobile_w41.avif" alt="hero image" className="sm:hidden block w-full h-full rounded-lg object-contain" />
                           
                            </div>
                        </div>

                        {/* CTA Container - positioned at bottom on mobile */}
                        {customCTAs ? (
                            customCTAs
                        ) : (
                        <CTAGroup orientation="vertical" style={{ zIndex: 20 }}
                            className={`w-full  order-1 sm:w-auto px-4 sm:px-0 sm:relative  left-0 right-0 sm:bottom-auto sm:left-auto sm:right-auto mt-6 z-20`}>
                            {showEmailInput ? (
                                // Lean/Instant variant: Only show email input field directly (no buttons)
                                <div className="w-full sm:w-auto">
                                  {success ? (
                                    <EmailSignupSuccess
                                      onRetry={() => {
                                        setSuccess(false)
                                        setEmail('')
                                        setError(null)
                                        setTimeout(() => {
                                          emailInputRef.current?.focus()
                                        }, 100)
                                      }}
                                    />
                                  ) : (
                                    <form id="hero-email-form" onSubmit={handleEmailSubmit} className="w-full sm:w-auto">
                                      <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                                        <div className="relative flex items-center w-full sm:w-auto">
                                          <input
                                            ref={emailInputRef}
                                            id="hero-email-input"
                                            type="email"
                                            value={email}
                                            autoFocus={true}
                                            onChange={(e) => {
                                              setEmail(e.target.value)
                                              setError(null)
                                            }}
                                            placeholder={emailPlaceholder}
                                            disabled={isLoading}
                                            className={cn(
                                              "px-4 py-2.5 text-sm rounded-l-md rounded-r-none border border-white/20 bg-black/50 text-white placeholder:text-white/60",
                                              "outline-none h-[48px]",
                                              "disabled:opacity-50 disabled:cursor-not-allowed",
                                              "w-full sm:w-auto sm:min-w-[400px]"
                                            )}
                                            required
                                          />
                                          <button
                                            type="submit"
                                            disabled={isLoading || !email.trim()}
                                            className={cn(
                                              "py-2.5 text-sm font-semibold rounded-r-md rounded-l-none h-[48px]",
                                              submitButtonText ? "px-4" : "px-3",
                                              "gradient-button_VWO bg-white text-black hover:bg-gray-50 border-white border-l-0",
                                              "focus:outline-none focus:ring-2 focus:ring-white/50",
                                              "disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200",
                                              "whitespace-nowrap flex items-center justify-center",
                                              (isLoading || !email.trim()) && "opacity-50 cursor-not-allowed"
                                            )}
                                          >
                                            {isLoading ? (
                                              <span className="flex items-center gap-2">
                                                <span className="animate-spin">⏳</span>
                                                <span>{isInstantVariant ? 'Signing in...' : 'Sending...'}</span>
                                              </span>
                                            ) : (
                                              <span className="flex items-center gap-2">
                                                {submitButtonText && <span>{submitButtonText}</span>}
                                                <Send className="w-4 h-4" />
                                              </span>
                                            )}
                                          </button>
                                        </div>
                                        {error && (
                                          <p className="text-red-400 text-xs px-1">{error}</p>
                                        )}
                                      </div>
                                    </form>
                                  )}
                                </div>
                            ) : (
                                // Other variants: Show buttons as normal
                                <>
                                    <div className='flex items-center gap-4 flex-col sm:flex-row w-full'>
                                        <CTAButton
                                            id={BUTTON_IDS.GET_STARTED}
                                            href={CTA_HREF || APP_LOGIN_URL}
                                            variant="primary"
                                            size="hero"
                                            external={CTA_HREF ? (CTA_HREF.startsWith('#') || CTA_HREF.startsWith('http')) : true}
                                            className="w-full sm:w-auto"
                                            enableGoogleLogin={enableHeroGoogleLogin && !CTA_HREF}
                                            onClick={(e: React.MouseEvent<HTMLElement>) => handleAnchorClick(e, CTA_HREF)}
                                        >
                                            {CTA}
                                        </CTAButton>
                                        <CTAButton
                                            id='book-demo-button'
                                            href={CTA_BOOK_DEMO_HREF || "https://calendly.com/hypertask/hypertask-ai/"}
                                            variant="outline"
                                            size="hero"
                                            external={CTA_BOOK_DEMO_HREF ? (CTA_BOOK_DEMO_HREF.startsWith('#') || CTA_BOOK_DEMO_HREF.startsWith('http')) : true}
                                            className="w-full sm:w-auto bg-black border-white/50 border sm:border"
                                            onClick={(e: React.MouseEvent<HTMLElement>) => handleAnchorClick(e, CTA_BOOK_DEMO_HREF)}
                                        >
                                            {CTA_BOOK_DEMO}
                                        </CTAButton>
                                    </div>
                                    <p className="text-white font-semibold tracking-tighter text-base mb-1">
                                        🔥 Where focus meets collaboration.
                                    </p>
                                </>
                            )}
                        </CTAGroup>
                        )}

                    </Container>
                </div>
            </section>
        </>
    )
}

export default HeroW45